import { Link } from "react-router-dom";
import { ArrowRight, Clock, HeartHandshake, Users, Gift } from "lucide-react";
import Seo from "@/components/Seo";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import BuyButton from "@/components/BuyButton";
import { IMAGES, GIFT_AUDIENCES, SENIORS_KIT_IMAGE } from "@/data/content";

const Home = () => {
  return (
    <div data-testid="home-page">
      <Seo
        title=""
        description="Discover UNSTUCK by Tammi, a woman-owned business offering 15-minute activity cards and interactive Seniors Game Kits designed to encourage connection, engagement, and inclusive participation."
        path="/"
      />
      {/* HERO */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="animate-fade-up">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-5">
              UNSTUCK Seniors — the featured collection
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-ink mb-6">
              Small moments. Meaningful connection.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-10 max-w-xl">
              Simple 15-minute activity cards designed to help older adults take one small step, stay
              engaged, connect, participate, and enjoy meaningful moments throughout the day.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <BuyButton
                lookupKey="seniors_program"
                testId="hero-shop-program-button"
                className="bg-sage text-white hover:bg-sage-dark shadow-[0_2px_10px_rgba(74,93,78,0.2)]"
              >
                Shop the Full Seniors Program <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </BuyButton>
              <BuyButton
                lookupKey="seniors_deck"
                testId="hero-shop-deck-button"
                className="bg-gold text-ink hover:bg-gold-dark hover:text-white"
              >
                Shop an Individual Seniors Deck
              </BuyButton>
              <Link
                to="/collections"
                data-testid="hero-explore-collections-button"
                className="inline-flex items-center justify-center gap-2 border-2 border-sage text-sage hover:bg-sage hover:text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-2 focus:ring-offset-cream"
              >
                Explore All UNSTUCK Collections
              </Link>
            </div>
          </div>
          <div className="grid gap-6">
            <ProductPlaceholder label="UNSTUCK Seniors product photo" ratio="aspect-[4/3]" testId="hero-product-placeholder" src={SENIORS_KIT_IMAGE} alt="The complete UNSTUCK Seniors Program: four card decks, printed instructions, Reward Bucks guide, and UNSTUCK Bucks" />
            <img
              src={IMAGES.hero}
              alt="Happy, diverse older adults talking and smiling together in a community setting"
              data-testid="hero-lifestyle-image"
              className="w-full aspect-[16/9] object-cover rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* FREE TRIAL BAND — prominent, near the top */}
      <section className="bg-sage">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14 sm:py-16 text-center">
          <p className="text-sm uppercase tracking-widest font-semibold text-gold mb-4">
            For senior communities, activity directors &amp; caregivers
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-white mb-4">
            Try UNSTUCK free for 14 days
          </h2>
          <p className="text-lg sm:text-xl text-cream/90 max-w-3xl mx-auto mb-8">
            Experience 14 complimentary UNSTUCK Seniors activity cards, created by an activity director to
            help residents of different abilities connect, participate, and enjoy meaningful 15-minute
            activities.
          </p>
          <Link
            to="/try-free"
            data-testid="home-free-trial-button"
            className="inline-flex items-center justify-center gap-2 bg-gold text-ink hover:bg-gold-dark hover:text-white rounded-full px-10 py-5 text-xl font-semibold transition-all duration-300 shadow-[0_2px_10px_rgba(0,0,0,0.15)] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-sage"
          >
            Try UNSTUCK Free for 14 Days
          </Link>
          <p className="font-heading italic text-lg text-cream/85 mt-8">
            "Created by an Activity Director. Designed with Every Resident in Mind."
          </p>
        </div>
      </section>

      {/* INCLUSION — near top, visible on entry */}
      <section className="bg-gold-light border-y border-gold/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Made for every ability</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-5">
              Simple, flexible, and inclusive — by design.
            </h2>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-4" data-testid="inclusion-message">
              A senior does not have to be highly mobile, verbal, or fully independent to take part.
              Some cards involve movement; others involve conversation, memory, hydration, reflection,
              choice, or everyday tasks — and every activity can be adapted to the person's abilities.
            </p>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-4">
              With support from a caregiver, family member, activity coordinator, or helper, everyone has
              a way in — and residents who participate with assistance still receive full participation
              credit and can earn UNSTUCK Bucks.
            </p>
            <p className="font-heading italic text-xl text-sage">
              Participation is about inclusion, not perfection.
            </p>
          </div>
        </div>
      </section>

      {/* SIMPLE ON PURPOSE */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Simple on purpose</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-6">
              One small action is the whole idea.
            </h2>
            <p className="text-lg leading-relaxed text-ink/80 mb-5">
              UNSTUCK is a collection of simple 15-minute activity decks designed to help people take one
              small action when they feel stuck, bored, overwhelmed, disconnected, or unsure where to begin.
            </p>
            <p className="text-lg leading-relaxed text-ink/80 mb-5">
              A prompt such as cleaning your glasses, organizing a small space, drinking water, or starting
              a conversation may seem small. The purpose is to help someone start, participate, engage, and
              build momentum — one manageable step at a time.
            </p>
            <div className="bg-cream border-l-4 border-sage rounded-r-2xl p-6" data-testid="participation-note">
              <p className="text-lg leading-relaxed text-ink/80 mb-4">
                UNSTUCK is designed so more people can participate. Not every person can walk, exercise,
                stand for long periods, or use their hands the same way. That's why the deck includes a
                variety of simple prompts — some involve movement, while others may involve conversation,
                memory, hydration, reflection, choice, or everyday tasks.
              </p>
              <p className="text-lg leading-relaxed text-ink/80">
                The goal is not to make every activity difficult. The goal is to give each person a way to
                take part, feel included, and complete one manageable action at a time.
              </p>
            </div>
          </div>
          <div className="grid gap-6">
            {[
              { icon: Clock, title: "15 minutes", text: "The basic reset window. Less or more is fine — there is no clock watching." },
              { icon: HeartHandshake, title: "Made for real life", text: "Everyday actions anyone can do, at their own pace, with no pressure to be perfect." },
              { icon: Users, title: "Alone or together", text: "Use a deck solo, with a caregiver or family member, partner-to-partner, or in a group." },
            ].map((item) => (
              <div key={item.title} className="flex gap-5 items-start bg-cream rounded-2xl p-6 border border-border/60">
                <div className="h-12 w-12 shrink-0 rounded-full bg-sage-light flex items-center justify-center">
                  <item.icon className="h-6 w-6 text-sage" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-ink mb-1">{item.title}</h3>
                  <p className="text-base leading-relaxed text-ink/75">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED: UNSTUCK SENIORS */}
      <section className="bg-sage-light">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
          <img
            src={IMAGES.group}
            alt="Two older men enjoying a game together"
            data-testid="home-seniors-lifestyle-image"
            className="w-full aspect-[4/3] object-cover rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] order-2 lg:order-1"
            loading="lazy"
          />
          <div className="order-1 lg:order-2">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Featured collection</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-6">
              UNSTUCK Seniors
            </h2>
            <p className="text-lg leading-relaxed text-ink/80 mb-5">
              Designed for older adults living independently, residents in assisted living or senior
              communities, caregivers, family members, senior centers, housing authorities, activity
              directors, senior program staff, and small groups.
            </p>
            <p className="text-lg leading-relaxed text-ink/80 mb-8">
              It is individual-first: a senior does not have to participate in a group to use UNSTUCK.
              The deck works alone, with a caregiver or family member, with another resident, or in a
              group setting.
            </p>
            <Link
              to="/seniors"
              data-testid="home-discover-seniors-button"
              className="inline-flex items-center justify-center gap-2 bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-[0_2px_10px_rgba(74,93,78,0.2)] focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-2"
            >
              Discover UNSTUCK Seniors <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* GIFTS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">A thoughtful gift</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-6">
              One small deck. A genuinely useful gift.
            </h2>
            <p className="text-lg leading-relaxed text-ink/80 mb-10">
              Individual UNSTUCK decks make thoughtful gifts for the people you care about — something
              they can pick up any day, for any mood, in just a few minutes.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="gifts-grid">
            {GIFT_AUDIENCES.map((audience) => (
              <div
                key={audience}
                className="flex items-center gap-3 bg-gold-light rounded-2xl px-5 py-4 border border-gold/30"
              >
                <Gift className="h-5 w-5 text-gold-dark shrink-0" aria-hidden="true" />
                <span className="text-base font-medium text-ink">{audience}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET THE CREATOR */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24 grid gap-12 lg:grid-cols-3 lg:items-center">
          <div className="lg:col-span-1">
            <img
              src="/images/floral-greenery.jpg"
              alt="Soft green leaves on a light background"
              data-testid="creator-photo"
              className="w-full max-w-md aspect-[4/3] object-cover rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-2">
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Meet the creator</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-ink mb-2">
              Hi, I'm Tammi.
            </h2>
            <p className="text-base uppercase tracking-widest font-semibold text-sage mb-6">
              Founder &amp; Creator of UNSTUCK | Activity Director
            </p>
            <p className="text-lg leading-relaxed text-ink/80 mb-5 max-w-2xl">
              As an activity director, I created UNSTUCK Seniors after seeing how residents with different
              mobility levels and physical abilities sometimes struggled to participate in traditional
              activities.
            </p>
            <p className="text-lg leading-relaxed text-ink/80 mb-5 max-w-2xl">
              I wanted to create something meaningful for residents who enjoy independent activities, as
              well as residents who need assistance. UNSTUCK allows residents to participate in their own
              way — whether independently, with staff support, or through someone completing an appropriate
              activity on their behalf.
            </p>
            <p className="text-lg leading-relaxed text-ink/80 max-w-2xl">
              Every resident deserves a chance to participate, feel included, and receive recognition.
            </p>
            <p className="font-heading italic text-2xl text-sage mt-6">
              "You are not stuck. You are becoming."
            </p>
          </div>
        </div>
      </section>

      {/* ORGANIZATIONS BAND */}
      <section className="bg-sage">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight text-white mb-4">
              For senior communities and organizations
            </h2>
            <p className="text-lg leading-relaxed text-cream/90">
              Special organization pricing is available for orders of 10 or more programs. Contact us for
              a custom quote.
            </p>
          </div>
          <Link
            to="/organizations"
            data-testid="home-organization-pricing-button"
            className="inline-flex shrink-0 items-center justify-center gap-2 bg-gold text-ink hover:bg-gold-dark hover:text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-sage"
          >
            Request Organization Pricing <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* FUTURE REVIEWS PLACEHOLDER */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div
            data-testid="reviews-placeholder"
            className="rounded-2xl border-2 border-dashed border-sage/40 bg-white p-10 sm:p-14 text-center"
          >
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-3">Coming soon</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-ink mb-3">
              Real reviews from real customers
            </h2>
            <p className="text-lg text-ink/70 max-w-2xl mx-auto">
              This space is reserved for genuine customer reviews. They will appear here as soon as the
              first customers share their experiences.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
